import type {BuildMediaSourceGroupsOptions, MediaSourceGroupNode, MediaSourceOption} from '@Components/media-source-menu/media-source-menu.types';
import {MediaSourceGroup, MediaSourceKind} from '@Components/media-source-menu/media-source-menu.types';
import {assertNever} from '@Utils/assert-never.util';

const ALL_GROUPS = [MediaSourceGroup.IMAGE, MediaSourceGroup.VIDEO];
const ALL_KINDS = [MediaSourceKind.UPLOADS, MediaSourceKind.STOCK, MediaSourceKind.AI];

const isImageGroup = (group: MediaSourceGroup): boolean => {
  return group === MediaSourceGroup.IMAGE;
};

const getGroupLabel = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? window.i18next.t('pmwjs_image') : window.i18next.t('pmwjs_video');
};

const getGroupIcon = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? 'icon-image' : 'icon-video';
};

const getGroupSubText = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? window.i18next.t('pmwjs_add_an_image_background') : window.i18next.t('pmwjs_add_a_video_background');
};

const getStockLabel = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? window.i18next.t('pmwjs_stock_photo') : window.i18next.t('pmwjs_stock_videos');
};

const getStockIcon = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? 'icon-stock-image' : 'icon-stock-video';
};

const getStockSubText = (group: MediaSourceGroup): string => {
  return isImageGroup(group) ? window.i18next.t('pmwjs_search_add_stock_photos') : window.i18next.t('pmwjs_search_add_stock_videos');
};

const getAiLabel = (group: MediaSourceGroup, useStickerLabel: boolean): string => {
  if (!isImageGroup(group)) {
    return window.i18next.t('pmwjs_ai_videos');
  }
  return window.i18next.t(useStickerLabel ? 'pmwjs_ai_stickers_caps' : 'pmwjs_ai_images');
};

const getAiIcon = (group: MediaSourceGroup, useStickerLabel: boolean): string => {
  if (!isImageGroup(group)) {
    return 'icon-video-ai';
  }
  return useStickerLabel ? 'icon-ai-sticker' : 'icon-ai-image';
};

const buildOption = (group: MediaSourceGroup, kind: MediaSourceKind, options: BuildMediaSourceGroupsOptions): MediaSourceOption => {
  const leaf = {group, kind};
  const base = {
    leaf,
    id: `media-source-${group}-${kind}`,
    isAi: kind === MediaSourceKind.AI,
    onSelect: (): void => {
      options.onSelect(leaf);
    },
  };

  switch (kind) {
    case MediaSourceKind.UPLOADS:
      return {...base, text: window.i18next.t('pmwjs_upload_media'), icon: 'icon-upload', subText: window.i18next.t('pmwjs_add_from_your_uploads')};
    case MediaSourceKind.STOCK:
      return {...base, text: getStockLabel(group), icon: getStockIcon(group), subText: getStockSubText(group)};
    case MediaSourceKind.AI: {
      const useStickerLabel = options.useStickerLabelForAiImage ?? false;
      return {
        ...base,
        text: getAiLabel(group, useStickerLabel),
        icon: getAiIcon(group, useStickerLabel),
        subText: window.i18next.t('pmwjs_create_with_ai'),
      };
    }
    default:
      return assertNever(kind);
  }
};

/**
 * The canonical media menu: Image / Video, each expanding to Upload media, stock, and AI. Every
 * background and replace surface renders this same tree so the option set cannot drift between them.
 */
export const buildMediaSourceGroups = (options: BuildMediaSourceGroupsOptions): MediaSourceGroupNode[] => {
  const groups = options.groups ?? ALL_GROUPS;
  const kinds = options.kinds ?? ALL_KINDS;

  return groups.map((group): MediaSourceGroupNode => {
    return {
      group,
      id: `media-source-group-${group}`,
      text: getGroupLabel(group),
      icon: getGroupIcon(group),
      subText: getGroupSubText(group),
      options: kinds.map((kind) => {
        return buildOption(group, kind, options);
      }),
    };
  });
};

/**
 * One group's leaves, unnested — for surfaces where a third level would be too deep, namely the
 * right-click context menu where Replace is already a submenu. Takes a single group rather than an
 * array because two groups flattened would yield two identically-labelled "Upload media" rows; the
 * picker each leaf opens still offers both media types, so nothing becomes unreachable.
 */
export const buildFlatMediaSourceOptions = (group: MediaSourceGroup, options: Omit<BuildMediaSourceGroupsOptions, 'groups'>): MediaSourceOption[] => {
  const [groupNode] = buildMediaSourceGroups({...options, groups: [group]});
  return groupNode?.options ?? [];
};
